/**
 * School Mobile App Setup API Contract
 *
 * GET  /v1/schools/:school_id/mobile-app/setup
 * POST /v1/schools/:school_id/mobile-app/setup
 *
 * A school with no live app of its own fills a guided setup before School
 * Journey provisions anything (release lines U10, R17, KTD12). Starting the
 * setup (POST) creates the school's app row, inactive and in `PROVISIONING`,
 * with no bundle id or store identifiers, so every school editor route has an
 * app to write to. Starting again returns the same row. Staff provisioning
 * adopts that row rather than creating a second one.
 *
 * `state` tells the web which surface to show:
 *  - `not-started`: no app row; offer to start the setup.
 *  - `in-setup`: the app is still `PROVISIONING`; show the setup.
 *  - `live`: the school has an app past provisioning; send it to the normal
 *    editor. A POST in this state creates nothing.
 */

import { z } from 'zod'
import { SchoolAppSetupCompletenessSchema } from '../../schemas/entities/school-app-setup'

export const SCHOOL_APP_SETUP_STATES = ['not-started', 'in-setup', 'live'] as const
export const SchoolAppSetupStateSchema = z.enum(SCHOOL_APP_SETUP_STATES)
export type SchoolAppSetupState = z.infer<typeof SchoolAppSetupStateSchema>

export const SchoolAppSetupResponseSchema = z.object({
  state: SchoolAppSetupStateSchema,
  /** `MobileApp.publicId`; null only in `not-started`. */
  appId: z.string().nullable(),
  /** True only on the POST that created the row. */
  created: z.boolean(),
  /** Which setup fields are filled; present only in `in-setup`. */
  completeness: SchoolAppSetupCompletenessSchema.nullable()
})
export type SchoolAppSetupResponse = z.infer<typeof SchoolAppSetupResponseSchema>
